import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Button } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery, useMutation } from "@octopusdeploy/octopus-react-client";
import { CommunicationStyleValues, EndpointsHelper } from "@octopusdeploy/octopus-server-client";
import type { KubernetesTentacleEndpointResource, DeploymentTargetResource, MachineResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import moment from "moment";
import * as React from "react";
import { useState } from "react";
import { getServerGrpcUri, type MonitorConfiguration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/configurationUtils";
import { client } from "~/clientInstance";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { PagedOnboardingDialogPageData } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { PagedOnboardingDialog } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { createMonitorUpgradeHelmCommand } from "../createHelmCommand";
import { useRefreshingAccessToken } from "../useRefreshingBearerToken";
import { KubernetesAgentSelectionPage } from "./KubernetesAgentSelectionPage";
import { KubernetesMonitorUpgradePage } from "./KubernetesMonitorUpgradePage";
import { getKubernetesMonitorDialogErrorPage } from "./kubernetesMonitorDialogErrorPage";
export interface KubernetesAgentTargetMonitorUpgradeDialogProps {
    spaceId: string;
    open: boolean;
    closeDialog: () => void;
    machine?: MachineResource;
}
export function KubernetesAgentTargetMonitorUpgradeDialog({ spaceId, open, closeDialog, machine }: KubernetesAgentTargetMonitorUpgradeDialogProps) {
    const [agentSelection, setAgentSelection] = useState<string | undefined>(machine?.Name);
    const { accessToken, accessTokenExpiry, refreshAccessToken } = useRefreshingAccessToken({});
    const accessTokenExpired = accessTokenExpiry < moment();
    const { navigate } = useSpaceAwareNavigation();
    const { result: agents, error } = useInlineStatusQuery(async (repository) => {
        const agents = await repository.Machines.list({ deploymentTargetTypes: CommunicationStyleValues.KubernetesTentacle });
        // Only auto select the first agent if we haven't explicitly provided one already
        if (!machine?.Name && agents.Items.length > 0) {
            setAgentSelection(agents.Items[0].Name);
        }
        return agents.Items;
    }, [machine], "KubernetesAgentTargetMonitorUpgradeDialog");
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create health check task",
        action: (repo) => (machine ? repo.Tasks.createHealthCheckTaskForMachine(machine) : Promise.resolve(undefined)),
        afterComplete: async (_, createdTask) => createdTask && navigate(links.taskPage.generateUrl({ taskId: createdTask.Id })),
    });
    const configuration: MonitorConfiguration = {
        SpaceId: spaceId,
        Name: machine?.Name || agentSelection || "",
        ServerUri: client.resolve("/"),
        AccessToken: accessToken,
        ServerGrpcUri: getServerGrpcUri(),
    };
    const selectedAgent = agents?.find((agent) => agent.Name === agentSelection);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const selectedAgentDetails = (selectedAgent?.Endpoint as KubernetesTentacleEndpointResource)?.KubernetesAgentDetails ?? null;
    const pages: PagedOnboardingDialogPageData[] = [];
    if (error) {
        return <PagedOnboardingDialog open={open} close={closeDialog} pages={[getKubernetesMonitorDialogErrorPage(error)]} name={"Install Kubernetes monitor"} showPageIndicator={false} hidePreviousButton={false} hideCompleteButton={false}/>;
    }
    if (!machine?.Name) {
        pages.push(...getAgentSelectionPages(agents, spaceId, agentSelection, setAgentSelection));
    }
    pages.push({
        name: "Upgrade Kubernetes agent to support live status",
        title: "Upgrade Kubernetes agent to support live status",
        helpPanelHidden: true,
        render: () => (<KubernetesMonitorUpgradePage accessTokenExpired={accessTokenExpiry < moment()} accessTokenExpiry={accessTokenExpiry} foundAgentDetails={!!selectedAgentDetails} generateHelmCommand={(selectedLanguage) => createMonitorUpgradeHelmCommand(selectedLanguage, selectedAgentDetails?.HelmReleaseName || "not-found", selectedAgentDetails?.KubernetesNamespace || "not-found", configuration)}/>),
        alwaysShowCloseButton: true,
        customPrimaryButton: accessTokenExpired ? (<ActionButton label="Regenerate token" onClick={refreshAccessToken} type={ActionButtonType.Primary}/>) : (<Button size="medium" importance="primary" onClick={createHealthCheckTask} label="Run health check" disabled={isCreatingHealthCheckTask}/>),
    });
    return <PagedOnboardingDialog open={open} close={closeDialog} pages={pages} name={"Install Kubernetes monitor"} showPageIndicator={false} hidePreviousButton={false} hideCompleteButton={false}/>;
}
function getAgentSelectionPages(agents: DeploymentTargetResource[] | null, spaceId: string, agentSelection: string | undefined, setAgentSelection: (newValue: string | undefined) => void): PagedOnboardingDialogPageData[] {
    const agentsWithoutMonitor = agents?.filter((agent) => EndpointsHelper.isKubernetesTentacle(agent.Endpoint) && !hasMonitorInstalled(agent.Endpoint));
    const pages: PagedOnboardingDialogPageData[] = [];
    if (agents && agents.length == 0) {
        pages.push({
            name: "Install a Kubernetes agent to get live status",
            title: "Install a Kubernetes agent to get live status",
            helpPanelHidden: true,
            render: () => (<div>
                    <p className={styles.paragraphWithSpace}>We couldn't find any upgradeable Kubernetes agents.</p>
                    <p>Would you like to install a new agent to get live status?</p>
                </div>),
            alwaysShowCloseButton: true,
            onlyShowCloseButton: true,
            customPrimaryButton: <Button label={"Install Agent"} importance="primary" href={links.selectDeploymentTargetTypePage.generateUrl({ spaceId })}/>,
        });
    }
    if (agentsWithoutMonitor && agentsWithoutMonitor.length == 0) {
        pages.push({
            name: "Install a Kubernetes agent to get live status",
            title: "Install a Kubernetes agent to get live status",
            helpPanelHidden: true,
            render: () => (<div className={styles.paragraphWithSpace}>
                    <p className={styles.paragraphWithSpace}>All Kubernetes agents have the monitor component enabled.</p>
                    <p>Would you like to install a new agent to get live status?</p>
                </div>),
            alwaysShowCloseButton: true,
            onlyShowCloseButton: true,
            customPrimaryButton: <Button label={"Install Agent"} importance="primary" href={links.selectDeploymentTargetTypePage.generateUrl({ spaceId })}/>,
        });
    }
    if (agentsWithoutMonitor && agentsWithoutMonitor.length > 0) {
        pages.push({
            name: "Upgrade Kubernetes agent to support live status",
            title: "Upgrade Kubernetes agent to support live status",
            helpPanelHidden: true,
            render: () => <KubernetesAgentSelectionPage agents={agentsWithoutMonitor} selectedAgent={agentSelection} onSelectionChanged={setAgentSelection}/>,
            alwaysShowCloseButton: true,
            onlyShowCloseButton: !agentSelection,
        });
    }
    return pages;
}
function hasMonitorInstalled(endpoint: KubernetesTentacleEndpointResource): boolean {
    return endpoint.KubernetesAgentDetails?.KubernetesMonitorEnabled ?? false;
}
const styles = {
    paragraphWithSpace: css({ paddingBottom: space[16] }),
};
